import { AfterViewInit, Directive, ElementRef, Input, OnDestroy, inject } from '@angular/core';
import { Select } from 'primeng/select';
import { MultiSelect } from 'primeng/multiselect';

/**
 * Diretiva que dá foco automático ao editor assim que ele entra em modo de edição.
 *
 * Problema resolvido:
 * Nos campos click-to-edit (label → editor via `@if (editingField() === '...')`),
 * o editor era renderizado sem foco. O usuário precisava de um segundo clique:
 * um para abrir a edição e outro para abrir a listagem / entrar no input.
 *
 * Comportamento por tipo de host:
 * - `p-select` / `p-multiSelect`: abre o overlay. Campos com `[filter]` já focam
 *   o input de busca ao exibir o overlay via `autofocusFilter` — default do
 *   PrimeNG no `Select`; no `MultiSelect` é preciso declarar a prop no template.
 * - `input` / `textarea` (`pInputText`, `p-inputNumber`, `p-datepicker`,
 *   `patron-chips`): foca o input e posiciona o cursor no fim do valor atual.
 *   No `p-datepicker` esse foco também abre o calendário (`showOnFocus`).
 *
 * O foco é aplicado em um `setTimeout` para rodar depois que o clique que
 * disparou a edição terminou de propagar — sem isso o listener de
 * document-click do Select fecharia o overlay recém-aberto.
 *
 * Uso:
 * ```html
 * <p-select appAutoEditFocus [filter]="true" appendTo="body" ... />
 * <input pInputText appAutoEditFocus [(ngModel)]="editRequester" />
 * ```
 */
@Directive({
  selector: '[appAutoEditFocus]',
  standalone: true,
})
export class AutoEditFocusDirective implements AfterViewInit, OnDestroy {
  /**
   * Permite desligar o auto-foco condicionalmente (ex.: `[appAutoEditFocus]="!bulkEditMode()"`)
   * sem remover a diretiva do template. Atributo estático (sem binding) continua valendo `true`.
   */
  @Input() appAutoEditFocus: boolean | '' = true;

  private readonly select = inject(Select, { optional: true, self: true });
  private readonly multiSelect = inject(MultiSelect, { optional: true, self: true });
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private timeoutId?: ReturnType<typeof setTimeout>;

  ngAfterViewInit(): void {
    if (this.appAutoEditFocus === false) {
      return;
    }
    this.timeoutId = setTimeout(() => this.activate());
  }

  ngOnDestroy(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }

  private activate(): void {
    const dropdown = this.select ?? this.multiSelect;

    if (dropdown) {
      this.openDropdown(dropdown);
      return;
    }

    this.focusInput();
  }

  /**
   * Abre o overlay do Select/MultiSelect. Com `[filter]` ativo, o próprio
   * PrimeNG foca o input de busca ao exibir o overlay desde que
   * `autofocusFilter` esteja habilitado.
   */
  private openDropdown(dropdown: Select | MultiSelect): void {
    dropdown.show();
  }

  /**
   * Localiza o input real do host e posiciona o cursor no fim do valor.
   * O host pode ser o próprio `<input>` (caso do `pInputText`) ou um
   * componente que encapsula um input (`p-inputNumber`, `p-datepicker`, ...).
   */
  private focusInput(): void {
    const element = this.host.nativeElement;
    const input =
      element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
        ? element
        : element.querySelector<HTMLInputElement | HTMLTextAreaElement>('input, textarea');

    if (!input) {
      return;
    }

    input.focus();

    // `setSelectionRange` não é suportado em alguns tipos (ex.: `type="email"`)
    // e lança InvalidStateError — nesses casos o `focus()` acima já basta.
    try {
      const end = input.value?.length ?? 0;
      input.setSelectionRange(end, end);
    } catch {
      // Tipo de input sem suporte a seleção: mantém apenas o foco.
    }
  }
}
